// First-party, human-only analytics beacons.
//
// Our custom event analytics used to be recorded server-side while rendering /
// redirecting, which counted every bot, crawler and link-prefetcher. These
// beacons move recording to real user interaction (JS), which bots don't run —
// so only humans are counted. They send no cookies and no personal data (just a
// signed reference to the event), so they run regardless of cookie consent.
//
// - Outbound clicks: fired on click of any tracking link. The link still works
//   without JS (it just won't be counted).
// - Pageviews: fired on load of an event page. The page opts in by setting
//   `window.__eaPageview = { token }` (see templates/events/_entry.twig).
// - Impressions: fired for cards actually scrolled into view. The page opts in by
//   setting `window.__eaImpressions = { token }` and marking each card with
//   `data-ea-impression="<eventId>"` (see _components/search/widget.twig).
import { onReady } from './dom.js';

// Exported so cookie-consent.js can use the same transport: sendBeacon where it exists,
// keepalive fetch where it does not. The consent beacon fires immediately before a
// reload, which is precisely the case sendBeacon exists for and a plain fetch loses.
export function beacon(path, params) {
    try {
        var body = new URLSearchParams(params);
        if (navigator.sendBeacon) {
            navigator.sendBeacon(path, body);
        } else {
            fetch(path, { method: 'POST', body: body, keepalive: true, credentials: 'same-origin' });
        }
    } catch (e) { /* never let tracking break the page */ }
}

// --- Outbound click tracking ---------------------------------------------
var OUTBOUND = '_event-analytics/reporting/outbound';

document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="' + OUTBOUND + '"]');
    if (!a) return;
    try {
        var q = new URL(a.href, window.location.origin).searchParams;
        trackBookingIntent(a, q.get('url'));
        if (!q.get('hash')) return;
        beacon('/actions/_event-analytics/reporting/record-click', {
            hash: q.get('hash'),
            entryId: q.get('entryId') || '',
            entryType: q.get('entryType') || '',
            url: q.get('url') || '',
            type: q.get('type') || '',
            // Part of the signed payload since #501; without it the server's signature
            // check fails and the click goes unrecorded. Empty on links signed before.
            module: q.get('module') || ''
        });
    } catch (err) {}
}, true);

// --- Booking intent (Matomo goal G3) ---------------------------------------
//
// Matomo can't see these clicks for itself: they go via our own outbound redirect, which
// it treats as an internal link, and the redirect serves no page. So a click on a link the
// template has labelled `data-matomo-outbound="<action>"` (on the link, or on a wrapper,
// which is how description links are labelled) pushes one Matomo event. That's what the
// "Booking intent" goal and funnel F2 match on. See #441.
//
// Category and action only: no event id, no name. Per-listing numbers belong to the
// first-party beacon above, which still records every click; this answers "did this visit
// end in intent to go", and will never match event_analytics_clicks, because of consent
// sampling. An unlabelled link isn't counted, so a new link counts only once someone
// decides it should. `_paq` exists only after consent on the main site, and templates
// label links only there.
function trackBookingIntent(a, destination) {
    // Its own try: a malformed destination must not cost the first-party beacon, which
    // shares the click handler.
    try {
        var labelled = a.closest('[data-matomo-outbound]');
        var action = labelled && labelled.getAttribute('data-matomo-outbound');
        if (!action || !window._paq) return;
        // A description link back into this site is navigation, not intent.
        if (destination) {
            var dest = new URL(destination, window.location.origin);
            if (/^https?:$/.test(dest.protocol) && dest.host === window.location.host) return;
        }
        window._paq.push(['trackEvent', 'Outbound', action]);
    } catch (err) {}
}

// --- Zero-result search (Matomo, #420) ---------------------------------------
//
// The only measurement of what people came for and didn't find. An empty search's URL is
// identical to a full one's, so the listing marks its empty state with
// `data-matomo-no-results="<date preset>"` (_components/search/noResults.twig) and this
// sends one event for it. The filters applied ride along without being sent here: the
// Filter signature custom dimension is set before trackPageView, and Matomo attaches a
// set dimension to every later request on the page, this event included.
//
// The name is the date preset, normalised to the known list so a hand-typed `?date=` can't
// grow the report without limit. No push without `_paq` (declined consent), and the
// marker only renders on the main site.
var DATE_PRESETS = ['today', 'tomorrow', 'this-weekend', 'this-week', 'next-week',
    'next-weekend', 'this-month', 'next-30-days', 'next-90-days', 'custom'];

onReady(function () {
    try {
        var marker = document.querySelector('[data-matomo-no-results]');
        if (!marker || !window._paq) return;
        var preset = marker.getAttribute('data-matomo-no-results');
        window._paq.push(['trackEvent', 'Search', 'No results',
            DATE_PRESETS.indexOf(preset) !== -1 ? preset : 'other']);
    } catch (err) {}
});

// --- Pageview tracking ----------------------------------------------------
// The page sets `window.__eaPageview` in an inline script in the body, so read
// it once the document has been parsed rather than at module evaluation.
onReady(function () {
    if (window.__eaPageview && window.__eaPageview.token) {
        beacon('/actions/_event-analytics/reporting/record-pageview', {
            token: window.__eaPageview.token,
            // Where this visit came from. Sent raw and bucketed server-side into
            // listing/search/home/internal/external/direct -- the URL itself is never
            // stored, only the category. See Analytics::classifySource().
            referrer: document.referrer || ''
        });
    }
});

// --- Impression tracking --------------------------------------------------
//
// An impression is a card someone actually saw, not a card the server rendered. Until
// #390 this was counted server-side at render: 54 rows for a default listing, written for
// every crawler and prefetcher as well as every person, which is how the table reached
// 94.9M rows and 9.4 GB.
//
// Two things make this cheap. IntersectionObserver reports a card once, the first time it
// crosses into view, and each card is unobserved as soon as it has been counted. And the
// ids are batched rather than sent one at a time -- a listing is 54 cards and a user who
// scrolls to the bottom would otherwise fire 54 requests.
//
// Flushed on a short idle timer and again on pagehide, because someone who scrolls and
// leaves immediately should still be counted. visibilitychange is the reliable partner to
// pagehide on mobile Safari, where pagehide alone is not guaranteed.
var IMPRESSION_ENDPOINT = '/actions/_event-analytics/reporting/record-impressions';
var FLUSH_AFTER_MS = 1000;

// Half the card visible is the threshold: enough that it was on screen to be read,
// forgiving enough that a card straddling the fold still counts.
var SEEN_RATIO = 0.5;

onReady(function () {
    var config = window.__eaImpressions;
    if (!config || !config.token) return;

    var cards = document.querySelectorAll('[data-ea-impression]');
    if (!cards.length) return;

    var pending = [];
    var timer = null;

    function flush() {
        if (timer) { clearTimeout(timer); timer = null; }
        if (!pending.length) return;
        var ids = pending.join(',');
        pending = [];
        beacon(IMPRESSION_ENDPOINT, { token: config.token, ids: ids });
    }

    function schedule() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(flush, FLUSH_AFTER_MS);
    }

    // No IntersectionObserver: count nothing rather than guess. Undercounting an old
    // browser is better than reintroducing the render-time behaviour this replaced.
    if (typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
            var entry = entries[i];
            if (!entry.isIntersecting) continue;
            var id = entry.target.getAttribute('data-ea-impression');
            if (id) pending.push(id);
            // Once counted, never again: an impression is the first time a card was seen,
            // not the number of times it scrolled past.
            observer.unobserve(entry.target);
        }
        if (pending.length) schedule();
    }, { threshold: SEEN_RATIO });

    for (var i = 0; i < cards.length; i++) observer.observe(cards[i]);

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') flush();
    });
});
